/**
 * Genesys Cloud Integration Settings
 * - View and map queues to companies
 * - Import historical calls
 * - Monitor import job status
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { Card } from '@/components/ui/card';
import { 
  Settings, Download, RefreshCw, CheckCircle, XCircle, 
  Clock, AlertCircle, Filter, Building2, Calendar, Loader2
} from 'lucide-react';

interface QueueMapping {
  id: string;
  queue_id: string;
  queue_name: string;
  company_id: string | null;
  company_name?: string;
  last_synced_at: string;
  is_active: boolean;
}

interface Company {
  id: string;
  name: string;
}

interface ImportJob {
  id: string;
  start_date: string;
  end_date: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  total_conversations: number;
  processed_count: number;
  imported_count: number;
  error_message?: string;
  created_at: string;
  completed_at?: string;
}

export default function GenesysIntegration() {
  const [queues, setQueues] = useState<QueueMapping[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Import form
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadQueues(),
      loadCompanies(),
      loadImportJobs()
    ]);
    setLoading(false);
  };

  const loadQueues = async () => {
    const { data, error } = await supabase
      .from('genesys_queue_mappings')
      .select('*')
      .order('queue_name');
    
    if (error) {
      console.error('Error loading queues:', error);
      return;
    }

    setQueues(data || []);
  };

  const loadCompanies = async () => {
    const { data, error } = await supabase
      .from('companies')
      .select('id, name')
      .order('name');
    
    if (error) {
      console.error('Error loading companies:', error);
      return;
    }

    setCompanies(data || []);
  };

  const loadImportJobs = async () => {
    const { data, error } = await supabase
      .from('genesys_import_jobs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);
    
    if (error) {
      console.error('Error loading import jobs:', error);
      return;
    }

    setImportJobs(data || []);
  };

  const syncQueues = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('genesys-sync-queues');
      
      if (error) throw error;
      
      alert(`Successfully synced ${data.synced || 0} queues from Genesys`);
      await loadQueues();
    } catch (error) {
      console.error('Error syncing queues:', error);
      alert('Failed to sync queues. Check console for details.');
    } finally {
      setSyncing(false);
    }
  };

  const updateQueueMapping = async (queueId: string, companyId: string | null) => {
    const { error } = await supabase
      .from('genesys_queue_mappings')
      .update({ 
        company_id: companyId,
        is_active: companyId !== null
      })
      .eq('queue_id', queueId);
    
    if (error) {
      console.error('Error updating queue mapping:', error);
      alert('Failed to update queue mapping');
      return;
    }

    await loadQueues();
  };

  const startImport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setImporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('genesys-import-historical', {
        body: { 
          start_date: startDate,
          end_date: endDate
        }
      });
      
      if (error) throw error;
      
      alert(`Import job started! Job ID: ${data.job_id}`);
      setImportDialogOpen(false);
      setStartDate('');
      setEndDate('');
      await loadImportJobs();
    } catch (error) {
      console.error('Error starting import:', error);
      alert('Failed to start import. Check console for details.');
    } finally {
      setImporting(false);
    }
  };

  const getStatusIcon = (status: ImportJob['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'in_progress':
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ImportJob['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const unmappedQueues = queues.filter(q => !q.company_id);
  const mappedQueues = queues.filter(q => q.company_id);

  return (
    <AdminPageLayout
      title="⚙️ Genesys Cloud Integration"
      subtitle="Map queues to companies and import historical calls"
      actions={
        <>
          <button
            onClick={syncQueues}
            disabled={syncing}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync Queues
          </button>
          <button
            onClick={() => setImportDialogOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Import Historical Calls
          </button>
        </>
      }
    >
      {/* Queue Mappings */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Queue Mappings
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {mappedQueues.length} of {queues.length} queues mapped to companies
            </p>
          </div>
        </div>

        {/* Unmapped Queues Warning */}
        {unmappedQueues.length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900">Action Required</h4>
                <p className="text-sm text-yellow-800 mt-1">
                  {unmappedQueues.length} queue(s) are not mapped to any company. 
                  Calls from unmapped queues will not be properly attributed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Queue List */}
        <div className="space-y-3">
          {queues.map((queue) => (
            <div
              key={queue.id}
              className={`border-2 rounded-lg p-4 ${
                queue.company_id 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-yellow-300 bg-yellow-50'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900">{queue.queue_name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      queue.company_id 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {queue.company_id ? 'Mapped' : 'Unmapped'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-mono mb-2">
                    Queue ID: {queue.queue_id}
                  </p>
                  {queue.last_synced_at && (
                    <p className="text-xs text-gray-500">
                      Last synced: {new Date(queue.last_synced_at).toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="w-64">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Assign to Company
                  </label>
                  <select
                    value={queue.company_id || ''}
                    onChange={(e) => updateQueueMapping(queue.queue_id, e.target.value || null)}
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                  >
                    <option value="">-- Select Company --</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}

          {queues.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Filter className="w-12 h-12 mx-auto mb-3" />
              <p>No queues found. Click "Sync Queues" to load from Genesys.</p>
            </div>
          )}
        </div>
      </Card>

      {/* Import Jobs */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Download className="w-5 h-5" />
              Import Jobs
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Historical call import status
            </p>
          </div>
          <button
            onClick={loadImportJobs}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        <div className="space-y-3">
          {importJobs.map((job) => (
            <div
              key={job.id}
              className="border-2 border-gray-300 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {getStatusIcon(job.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(job.status)}`}>
                        {job.status.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-600">
                        {new Date(job.start_date).toLocaleDateString()} - {new Date(job.end_date).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                      <div>
                        <span className="text-gray-600">Total:</span>
                        <span className="ml-2 font-semibold">{job.total_conversations}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Processed:</span>
                        <span className="ml-2 font-semibold">{job.processed_count}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Imported:</span>
                        <span className="ml-2 font-semibold text-green-600">{job.imported_count}</span>
                      </div>
                    </div>

                    {job.status === 'in_progress' && job.total_conversations > 0 && (
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${(job.processed_count / job.total_conversations) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {((job.processed_count / job.total_conversations) * 100).toFixed(1)}% complete
                        </p>
                      </div>
                    )}

                    {job.error_message && (
                      <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                        {job.error_message}
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">
                      Started: {new Date(job.created_at).toLocaleString()}
                      {job.completed_at && ` • Completed: ${new Date(job.completed_at).toLocaleString()}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {importJobs.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Download className="w-12 h-12 mx-auto mb-3" />
              <p>No import jobs yet. Start your first import above.</p>
            </div>
          )}
        </div>
      </Card>

      {/* Import Dialog */}
      {importDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Import Historical Calls
              </h3>
              <button
                onClick={() => setImportDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>Note:</strong> This will import all calls from mapped queues within the selected date range. 
                Large date ranges may take several minutes to process.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={() => setImportDialogOpen(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={startImport}
                  disabled={importing || !startDate || !endDate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting Import...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Start Import
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
}
